import { FC, Dispatch, SetStateAction, useEffect } from 'react'
import cn from 'classnames'
import { useTranslation } from 'next-i18next/pages'
import { useForm, SubmitHandler } from 'react-hook-form'
import { Wishlist, MutationUpdateWishlistArgs } from '@pylot-data/pylotschema'
import { WishlistInputVariable } from '../Hooks/use-create-wishlist'
import accountFieldS from '@components/account/AccountInformationPage/AccountInformationPage.module.scss'
import { Button, ConfirmBox } from '@corratech/pylot-ui'
import s from './CreateWishlistForm.module.scss'

export type Props = {
    show: boolean
    setShow: Dispatch<SetStateAction<boolean>>
    onSubmit: SubmitHandler<WishlistInputVariable & MutationUpdateWishlistArgs>
    wishlist?: Wishlist
    isLoading?: boolean
    fromWishlistSidebar?: boolean
}

const NAME_MAX_LENGTH = 30

export const CreateWishlistForm: FC<Props> = ({
    show,
    setShow,
    wishlist,
    onSubmit,
    isLoading = false,
    fromWishlistSidebar = false
}) => {
    const { t } = useTranslation(['wishlist'])

    const {
        reset,
        register,
        handleSubmit,
        watch,
        formState: { errors, isDirty }
    } = useForm<WishlistInputVariable & MutationUpdateWishlistArgs>({
        mode: 'onChange',
        defaultValues: {
            name: wishlist?.name ?? ''
        }
    })

    const nameValue = watch('name') ?? ''
    const nameLength =
        typeof nameValue === 'string'
            ? nameValue.length
            : String(nameValue).length
    const nameRemaining = Math.max(0, NAME_MAX_LENGTH - nameLength)

    useEffect(() => {
        if (show) {
            reset({ name: wishlist?.name ?? '' })
        }
    }, [show, wishlist?.id, wishlist?.name, reset])

    const creatingWishlistText = isLoading
        ? 'wishlist|Creating list'
        : 'wishlist|Create list'

    const wishlistActionText = wishlist
        ? 'wishlist|Update Wish List'
        : creatingWishlistText

    const wishlistInputLabel = fromWishlistSidebar
        ? 'wishlist|Create your wishlist'
        : 'wishlist|List name'

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            className={s[fromWishlistSidebar ? 'wishlist-form-sidebar' : '']}
        >
            <div className={s['wishlist-form-container']}>
                <div className={accountFieldS.field}>
                    <label
                        className={accountFieldS.label}
                        htmlFor="wishlist-create-name"
                    >
                        {t(wishlistInputLabel)}
                    </label>
                    <input
                        id="wishlist-create-name"
                        type="text"
                        autoComplete="off"
                        maxLength={NAME_MAX_LENGTH}
                        placeholder={t('wishlist|Wish list name placeholder')}
                        className={cn(
                            accountFieldS.input,
                            errors.name && accountFieldS.inputError
                        )}
                        {...register('name', { required: true })}
                    />
                    {errors.name && (
                        <span className={accountFieldS.errorText}>
                            {t('wishlist|This field is required')}
                        </span>
                    )}
                    <p
                        className={s.charCounter}
                        aria-live="polite"
                        aria-atomic="true"
                    >
                        {t('wishlist|List name characters remaining', {
                            count: nameRemaining,
                            max: NAME_MAX_LENGTH
                        })}
                    </p>
                </div>
            </div>
            <div className={s['form-actions']}>
                <Button
                    variant="primary"
                    aria-label={t('lista')}
                    className={s['button']}
                    disabled={isLoading || !isDirty || nameLength <= 2}
                    loading={isLoading}
                >
                    {t(wishlistActionText)}
                </Button>
                {!fromWishlistSidebar && (
                    <Button
                        type="button"
                        variant="secondary"
                        aria-label={t('crear lista')}
                        className={s['button']}
                        onClick={() => setShow(false)}
                        loading={isLoading}
                    >
                        {t('cancelar')}
                    </Button>
                )}
            </div>
        </form>
    )
}
